import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  AttachmentDto,
  AttachmentDtoKind,
  CollaboratorDto,
  MessageWithAttachmentsDto,
  MessageWithAttachmentsDtoStatus
} from '../../../services/api.clients.generated';
import { MarkdownHtmlPipe } from '../../../shared/markdown/markdown-html.pipe';
import { getContactAvatarColor, getContactInitials, getContactName } from '../../contact-display.utils';
import { formatAttachmentSize, getAttachmentColor, getAttachmentIcon, getAttachmentLabel } from '../../attachment-display.utils';
import { AssigneePickerComponent } from '../assignee-picker/assignee-picker.component';
import { StatusSelectComponent } from '../status-select/status-select.component';

/**
 * A single message in the conversation feed: sender avatar/name/email/time,
 * markdown body, the attachment row with per-message ICARE selection, and an
 * action row (assignee + status). Presentational only — state and actions are
 * owned by the parent.
 */
@Component({
  selector: 'app-message-card',
  imports: [DatePipe, MatIcon, MatTooltip, TranslatePipe, MarkdownHtmlPipe, AssigneePickerComponent, StatusSelectComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './message-card.component.html',
  styleUrl: './message-card.component.scss'
})
export class MessageCardComponent {
  private readonly translate = inject(TranslateService);
  protected readonly attachmentKinds = AttachmentDtoKind;

  readonly message = input.required<MessageWithAttachmentsDto>();
  readonly selectedAttachmentIds = input<number[]>([]);
  readonly blockedAttachmentIds = input<Set<number>>(new Set<number>());
  readonly collaborators = input<CollaboratorDto[]>([]);

  readonly openAttachment = output<AttachmentDto>();
  readonly toggleAttachment = output<AttachmentDto>();
  readonly selectAllToggle = output<MessageWithAttachmentsDto>();
  readonly sendToIcare = output<MessageWithAttachmentsDto>();
  readonly reply = output<MessageWithAttachmentsDto>();
  readonly transfer = output<MessageWithAttachmentsDto>();
  readonly assignCollaborator = output<{ message: MessageWithAttachmentsDto; collaborator: CollaboratorDto | null }>();
  readonly changeStatus = output<{ message: MessageWithAttachmentsDto; status: MessageWithAttachmentsDtoStatus }>();

  readonly initials = computed(() => getContactInitials(this.message()));
  readonly avatarColor = computed(() => getContactAvatarColor(this.message()));
  readonly senderName = computed(() =>
    getContactName(this.message(), this.translate.instant('common.unknown'))
  );

  /** Attachments that can still be selected for an ICARE upload. */
  readonly selectableAttachments = computed<AttachmentDto[]>(() => {
    const blocked = this.blockedAttachmentIds();
    return (this.message().attachments ?? []).filter(attachment =>
      attachment.kind === AttachmentDtoKind.FILE
      && attachment.id != null
      && !attachment.icareDocumentId
      && !blocked.has(attachment.id));
  });
  readonly hasIcareControls = computed(() => this.selectableAttachments().length > 0);
  readonly selectedCount = computed(() => {
    const selected = new Set(this.selectedAttachmentIds());
    return this.selectableAttachments().filter(attachment => attachment.id != null && selected.has(attachment.id)).length;
  });
  readonly allSelected = computed(() => {
    const total = this.selectableAttachments().length;
    return total > 0 && this.selectedCount() === total;
  });

  protected isSelected(attachment: AttachmentDto): boolean {
    return attachment.id != null && this.selectedAttachmentIds().includes(attachment.id);
  }

  protected isBlocked(attachment: AttachmentDto): boolean {
    return attachment.id != null && this.blockedAttachmentIds().has(attachment.id);
  }

  protected isSelectable(attachment: AttachmentDto): boolean {
    return attachment.kind === AttachmentDtoKind.FILE
      && attachment.id != null
      && !attachment.icareDocumentId
      && !this.isBlocked(attachment);
  }

  protected getAttachmentIcon(attachment: AttachmentDto): string {
    return getAttachmentIcon(attachment);
  }

  protected getAttachmentLabel(attachment: AttachmentDto): string {
    return getAttachmentLabel(attachment, this.translate.instant('common.unknown'));
  }

  protected getAttachmentColor(attachment: AttachmentDto): string {
    return getAttachmentColor(attachment);
  }

  protected formatSize(bytes: number | undefined): string {
    return formatAttachmentSize(bytes);
  }

  protected onAssigneeChange(collaborator: CollaboratorDto | null): void {
    this.assignCollaborator.emit({ message: this.message(), collaborator });
  }

  protected onStatusChange(status: MessageWithAttachmentsDtoStatus): void {
    this.changeStatus.emit({ message: this.message(), status });
  }
}
